import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Eye, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { adminApi } from '../api'
import { Btn, Card, TableSkeleton, inputClass } from '../ui'
import {
  CoverThumb,
  formatDate,
  IconAction,
  StatusBadge,
  type InsightRow,
} from './insightsShared'

const PAGE_SIZE = 10

export default function InsightsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<InsightRow[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<'all' | 'published' | 'draft'>('all')
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await adminApi<{ insights?: InsightRow[] }>('/api/admin/website/insights')
    setRows(data.insights || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const counts = useMemo(() => {
    const published = rows.filter((r) => r.is_published).length
    return { all: rows.length, published, draft: rows.length - published }
  }, [rows])

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase()
    return rows.filter((r) => {
      if (status === 'published' && !r.is_published) return false
      if (status === 'draft' && r.is_published) return false
      if (!query) return true
      return (
        r.title.toLowerCase().includes(query) ||
        r.slug.toLowerCase().includes(query) ||
        (r.summary || '').toLowerCase().includes(query)
      )
    })
  }, [rows, q, status])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [q, status])

  async function remove(id: number) {
    if (!confirm('Delete this insight?')) return
    await adminApi(`/api/admin/website/insights/${id}`, { method: 'DELETE' })
    load()
  }

  const from = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1
  const to = Math.min(safePage * PAGE_SIZE, filtered.length)

  const filterOptions = [
    { key: 'all' as const, label: 'All', count: counts.all },
    { key: 'published' as const, label: 'Published', count: counts.published },
    { key: 'draft' as const, label: 'Draft', count: counts.draft },
  ]

  return (
    <div className="space-y-4 sm:space-y-5">
      <Card className="!p-2.5 sm:!p-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className={`${inputClass} !pl-9 !pr-3 !py-2 h-10 sm:h-11`}
            />
          </div>

          <div
            className="inline-flex shrink-0 rounded-2xl border border-primary/15 bg-slate-50/80 p-0.5 gap-0.5"
            role="tablist"
            aria-label="Status filter"
          >
            {filterOptions.map((f) => {
              const active = status === f.key
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setStatus(f.key)}
                  className={`inline-flex items-center justify-center gap-1 rounded-xl px-2 sm:px-2.5 h-9 sm:h-10 text-xs sm:text-sm font-semibold transition whitespace-nowrap ${
                    active
                      ? 'bg-white text-primary shadow-sm ring-1 ring-primary/15'
                      : 'text-slate-500 hover:text-navy-900'
                  }`}
                >
                  <span className="hidden sm:inline">{f.label}</span>
                  <span className="sm:hidden">{f.key === 'all' ? 'All' : f.key === 'published' ? 'Pub' : 'Draft'}</span>
                  <span
                    className={`tabular-nums rounded-md px-1 py-0.5 text-[0.65rem] font-bold ${
                      active ? 'bg-primary/10 text-primary' : 'bg-slate-200/80 text-slate-600'
                    }`}
                  >
                    {f.count}
                  </span>
                </button>
              )
            })}
          </div>

          <Btn onClick={() => navigate('/admin/insights/new')}>
            <Plus className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Add</span>
          </Btn>
        </div>
        <p className="mt-2 text-xs text-slate-500 px-0.5">
          {loading
            ? 'Loading…'
            : filtered.length === 0
              ? 'No matches'
              : `Showing ${from}–${to} of ${filtered.length}`}
        </p>
      </Card>

      {loading ? (
        <TableSkeleton rows={8} cols={4} />
      ) : (
        <>
          <Card className="hidden md:block !p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-primary/10 bg-slate-50/80 text-left text-[0.7rem] font-bold uppercase tracking-[0.1em] text-slate-500">
                    <th className="px-4 py-3 w-14">Sr</th>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3 w-28">Status</th>
                    <th className="px-4 py-3 w-32">Published</th>
                    <th className="px-4 py-3 w-44 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, idx) => {
                    const sr = (safePage - 1) * PAGE_SIZE + idx + 1
                    return (
                      <tr key={r.id} className="border-b border-primary/5 last:border-0 hover:bg-primary/[0.03] transition">
                        <td className="px-4 py-3 tabular-nums text-slate-500 font-medium">{sr}</td>
                        <td className="px-4 py-3">
                          <Link to={`/admin/insights/${r.id}`} className="flex items-center gap-3 min-w-0 group">
                            <CoverThumb path={r.cover_image_path} />
                            <div className="min-w-0">
                              <div className="font-semibold text-navy-900 truncate group-hover:text-primary">{r.title}</div>
                              <div className="text-xs text-slate-400 truncate">/{r.slug}</div>
                            </div>
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge published={!!r.is_published} />
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(r.published_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1.5">
                            <IconAction label="View" onClick={() => navigate(`/admin/insights/${r.id}`)}>
                              <Eye className="h-4 w-4" />
                            </IconAction>
                            <IconAction label="Edit" onClick={() => navigate(`/admin/insights/${r.id}/edit`)}>
                              <Pencil className="h-4 w-4" />
                            </IconAction>
                            <IconAction label="Delete" danger onClick={() => remove(r.id)}>
                              <Trash2 className="h-4 w-4" />
                            </IconAction>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                        No insights found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="md:hidden space-y-3">
            {pageRows.map((r, idx) => {
              const sr = (safePage - 1) * PAGE_SIZE + idx + 1
              return (
                <Card key={r.id} className="!p-3.5">
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-bold text-primary tabular-nums">
                      {sr}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/admin/insights/${r.id}`} className="flex items-start gap-2.5">
                        <CoverThumb path={r.cover_image_path} />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-navy-900 leading-snug">{r.title}</div>
                          <div className="mt-0.5 text-xs text-slate-400 truncate">/{r.slug}</div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <StatusBadge published={!!r.is_published} />
                            <span className="text-xs text-slate-500">{formatDate(r.published_at)}</span>
                          </div>
                        </div>
                      </Link>
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <Btn variant="ghost" onClick={() => navigate(`/admin/insights/${r.id}`)}>
                          <Eye className="h-4 w-4" /> View
                        </Btn>
                        <Btn variant="ghost" onClick={() => navigate(`/admin/insights/${r.id}/edit`)}>
                          <Pencil className="h-4 w-4" /> Edit
                        </Btn>
                        <Btn variant="danger" onClick={() => remove(r.id)}>
                          <Trash2 className="h-4 w-4" /> Del
                        </Btn>
                      </div>
                    </div>
                  </div>
                </Card>
              )
            })}
            {pageRows.length === 0 && (
              <Card className="!py-10 text-center text-slate-500 text-sm">No insights found.</Card>
            )}
          </div>
        </>
      )}

      {filtered.length > PAGE_SIZE && (
        <Card className="!p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs sm:text-sm text-slate-500">
              Page <span className="font-semibold text-navy-900">{safePage}</span> of{' '}
              <span className="font-semibold text-navy-900">{totalPages}</span>
            </p>
            <div className="flex gap-2">
              <Btn variant="ghost" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Btn>
              <Btn
                variant="ghost"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Btn>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
